import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdminAuth } from '../../../context/AdminAuthContext';
import { useToast } from '../../../context/ToastContext';
import AdminLayout from '../layout';
import ProductsTab from '../../../component/admin/ProductCatalog/ProductsTab';
import CollectionsTab from '../../../component/admin/ProductCatalog/CollectionsTab';
import AddOnsTab from '../../../component/admin/ProductCatalog/AddonsTab';
import AddCollectionModal from '../../../component/admin/ProductCatalog/modal/AddCollectionModal';
import AddProductModal from '../../../component/admin/ProductCatalog/modal/AddProductModal';
import AddAddonModal from '../../../component/admin/ProductCatalog/modal/AddAddonModal';
import { PlusIcon } from '../../../public/icons';

// ✅ Import the separate services
import adminProductService from '../../../services/admin/admin-productService';
import adminCollectionService from '../../../services/admin/admin-collectionService';
import adminAddonService from '../../../services/admin/admin-addonService';

const ProductCatalog = () => {
  const navigate = useNavigate();
  const { admin, logout } = useAdminAuth();
  const { success, error } = useToast();
  const [activeTab, setActiveTab] = useState('products');
  const [loading, setLoading] = useState(false);
  
  // Modal states
  const [showCollectionModal, setShowCollectionModal] = useState(false);
  const [showProductModal, setShowProductModal] = useState(false);
  const [showAddonModal, setShowAddonModal] = useState(false);
  
  // Selected product for collections
  const [selectedProduct, setSelectedProduct] = useState(null);
  
  // Refresh triggers
  const [refreshProducts, setRefreshProducts] = useState(0);
  const [refreshCollections, setRefreshCollections] = useState(0);
  const [refreshAddons, setRefreshAddons] = useState(0);

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const tabs = [
    { 
      id: 'products', 
      label: 'All Products',
      buttonText: 'Add New Product',
      action: () => setShowProductModal(true)
    },
    { 
      id: 'collections', 
      label: 'Collections',
      buttonText: 'Add Collection',
      action: () => setShowCollectionModal(true)
    },
    { 
      id: 'addons', 
      label: 'Adds-On',
      buttonText: 'Add new add-on',
      action: () => setShowAddonModal(true)
    }
  ];

  const activeTabConfig = tabs.find(tab => tab.id === activeTab);

  const handleAddButtonClick = () => {
    if (activeTabConfig?.action) {
      activeTabConfig.action();
    }
  };

  // ============ PRODUCT HANDLERS ============
  const handleProductSelect = (product) => {
    setSelectedProduct(product);
    if (activeTab === 'collections') {
      success(`Now managing collections for: ${product.name}`);
    }
  };

  const handleProductSubmit = async (productData) => {
    try {
      setLoading(true);
      console.log('📤 [Frontend] Creating active product:', productData);
      
      const response = await adminProductService.createProduct(productData);
      
      if (response.success) {
        success('Product created successfully!');
        setSelectedProduct(response.data);
        setShowProductModal(false);
        setRefreshProducts(prev => prev + 1);
      } else {
        error(response.message || 'Failed to create product');
      }
    } catch (err) {
      console.error('💥 [Frontend] Create product error:', err);
      error('Failed to create product. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveDraft = async (productData) => {
    try {
      setLoading(true);
      console.log('📤 [Frontend] Saving product as draft:', productData);
      
      const response = await adminProductService.createProduct(productData);
      
      if (response.success) {
        success('Product saved as draft successfully!');
        setSelectedProduct(response.data);
        setShowProductModal(false);
        setRefreshProducts(prev => prev + 1);
      } else {
        error(response.message || 'Failed to save draft');
      }
    } catch (err) {
      console.error('💥 [Frontend] Save draft error:', err);
      error('Failed to save draft. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ============ COLLECTION HANDLERS ============
  const handleCollectionSubmit = async (collectionData, collectionType, status = 'active') => {
    try {
      setLoading(true);
      
      console.log('📤 [Frontend] Creating collection:', {
        collectionData,
        collectionType,
        status
      });

      const collectionPayload = { 
        ...collectionData,
        status: status
      };

      const response = await adminCollectionService.createCollection(collectionPayload);
      
      if (response.success) {
        success(`Collection ${status === 'draft' ? 'saved as draft' : 'created'} successfully!`);
        setShowCollectionModal(false);
        setRefreshCollections(prev => prev + 1);
      } else {
        error(response.message || 'Failed to create collection');
      }
    } catch (err) {
      console.error('💥 [Frontend] Create collection error:', err);
      error('Failed to create collection. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveCollectionDraft = async (collectionData, collectionType) => {
    await handleCollectionSubmit(collectionData, collectionType, 'draft');
  };

  // ============ ADDON HANDLERS ============
  const handleAddonSubmit = async (addonData) => {
    try {
      setLoading(true);
      
      console.log('📤 [Frontend] Creating addon:', addonData);

      const variantsWithImages = [];
      if (addonData.variants && addonData.variants.length > 0) {
        for (const variant of addonData.variants) {
          const variantCopy = { ...variant };
          
          if (variant.image && typeof variant.image !== 'object') {
            try {
              const cloudinaryResponse = await adminAddonService.uploadImageToCloudinary(variant.image);
              variantCopy.image = {
                url: cloudinaryResponse.url,
                publicId: variant.image.publicId || cloudinaryResponse.publicId
              };
            } catch (uploadError) {
              console.error('Variant image upload error:', uploadError);
              error(`Failed to upload image for variant: ${variant.name}`);
            }
          }
          
          variantsWithImages.push(variantCopy);
        }
      }

      const addonPayload = {
        name: addonData.name,
        basePrice: parseFloat(addonData.basePrice),
        shortDescription: addonData.shortDescription || '',
        isFunctional: addonData.isFunctional || false,
        contentType: addonData.isFunctional ? (addonData.contentType || 'audio') : 'none',
        status: addonData.status || 'active',
        variants: variantsWithImages.length > 0 ? variantsWithImages : addonData.variants || [],
        compatibleProducts: addonData.compatibleProducts || []
      };

      console.log('📤 [Frontend] Sending addon payload:', JSON.stringify(addonPayload, null, 2));

      const response = await adminAddonService.createAddon(addonPayload);
      
      if (response.success) {
        success('Add-on created successfully!');
        setShowAddonModal(false);
        setRefreshAddons(prev => prev + 1);
      } else {
        error(response.message || 'Failed to create add-on');
      }
    } catch (err) {
      console.error('💥 [Frontend] Create addon error:', err);
      error('Failed to create add-on. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const renderActiveTab = () => {
    switch (activeTab) {
      case 'products':
        return <ProductsTab 
          onProductSelect={handleProductSelect} 
          refreshTrigger={refreshProducts}
        />;
      case 'collections':
        return <CollectionsTab 
          productId={selectedProduct?.id || selectedProduct?._id}
          refreshTrigger={refreshCollections}
        />;
      case 'addons':
        return <AddOnsTab 
          refreshTrigger={refreshAddons}
        />;
      default:
        return <ProductsTab 
          onProductSelect={handleProductSelect}
          refreshTrigger={refreshProducts}
        />;
    }
  };

  return (
    <AdminLayout onLogout={handleLogout}>
      <div className="space-y-6">
        {/* Tab Navigation */}
        <div className="flex justify-between items-center">
          <div className="flex border-b-2">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-[#55000C] text-[#55000C]'
                    : 'border-transparent text-[#6B635D] hover:text-[#1C2A46]'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <button
            onClick={handleAddButtonClick}
            disabled={loading}
            className="px-5 py-3.5 bg-[#D2A146] text-white text-sm font-medium hover:bg-[#C19654] transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <PlusIcon size={16} color="#FFFFFF" />
            {loading ? 'Processing...' : (activeTabConfig?.buttonText || 'Add New Product')}
          </button>
        </div>

        {/* Selected Product Indicator */}
        {activeTab === 'collections' && selectedProduct && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-blue-800">
                  Managing collections for: <span className="font-bold">{selectedProduct.name}</span>
                </p>
                <p className="text-xs text-blue-600 mt-1">
                  Product Type: {selectedProduct.baseProduct || selectedProduct.typeOfProduct}
                </p>
              </div>
              <button
                onClick={() => setSelectedProduct(null)}
                className="text-xs text-blue-600 hover:text-blue-800"
              >
                Change Product
              </button>
            </div>
          </div>
        )}

        {/* Tab Content */}
        {renderActiveTab()}
      </div>

      {/* Modals */}
      <AddCollectionModal
        isOpen={showCollectionModal}
        onClose={() => setShowCollectionModal(false)}
        onSubmit={handleCollectionSubmit}
        onSaveDraft={handleSaveCollectionDraft}
        loading={loading}
        selectedProduct={selectedProduct}
      />

      <AddProductModal
        isOpen={showProductModal}
        onClose={() => setShowProductModal(false)}
        onSubmit={handleProductSubmit}
        onSaveDraft={handleSaveDraft}
        loading={loading}
      />

      <AddAddonModal
        isOpen={showAddonModal}
        onClose={() => setShowAddonModal(false)}
        onSubmit={handleAddonSubmit}
        loading={loading}
      />
    </AdminLayout>
  );
};

export default ProductCatalog;